/**
 * UI Component Utilities & Builders
 * Provides helper functions to create structured, reusable UI components.
 * Replaces inline HTML string concatenation with cleaner API.
 */

import { THEME } from './ui-framework';

// ============================================================================
// COMPONENT BASE CLASSES & UTILITIES
// ============================================================================

/**
 * Base class for UI components — handles DOM lifecycle, events, styling.
 */
export abstract class UIComponent {
  el: HTMLElement;

  constructor(tag: string = 'div', className?: string) {
    this.el = document.createElement(tag);
    if (className) this.el.className = className;
  }

  setStyle(styles: Record<string, string>): this {
    Object.assign(this.el.style, styles);
    return this;
  }

  addChild(child: UIComponent | HTMLElement): this {
    if (child instanceof UIComponent) {
      this.el.appendChild(child.el);
    } else {
      this.el.appendChild(child);
    }
    return this;
  }

  setText(text: string): this {
    this.el.textContent = text;
    return this;
  }

  on(event: string, handler: (ev: Event) => void): this {
    this.el.addEventListener(event, handler);
    return this;
  }

  addClass(className: string): this {
    this.el.classList.add(className);
    return this;
  }

  removeClass(className: string): this {
    this.el.classList.remove(className);
    return this;
  }

  show(visible: boolean = true): this {
    this.el.style.display = visible ? '' : 'none';
    return this;
  }
}

// ============================================================================
// COMMON LAYOUT BUILDERS
// ============================================================================

/**
 * Create a titled panel with border and padding.
 */
export class Panel extends UIComponent {
  header: HTMLElement | null = null;
  content: HTMLElement;

  constructor(title?: string) {
    super('div');
    this.content = document.createElement('div'); // Initialize before any code path
    this.setStyle({
      border: `1px solid ${THEME.border}`,
      borderRadius: '6px',
      background: THEME.sidebar,
      overflow: 'hidden',
    });

    if (title) {
      this.header = document.createElement('div');
      this.header.style.cssText = `
        padding: 12px 14px;
        background: #f5f5f2;
        border-bottom: 1px solid ${THEME.border};
        font-weight: 600;
        font-size: 12px;
        color: ${THEME.text};
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-family: var(--mono-font);
      `;
      this.header.textContent = title;
      this.el.appendChild(this.header);
    }

    this.content = document.createElement('div');
    this.content.style.cssText = 'padding: 12px 14px;';
    this.el.appendChild(this.content);
  }

  addContent(child: UIComponent | HTMLElement): this {
    if (child instanceof UIComponent) {
      this.content.appendChild(child.el);
    } else {
      this.content.appendChild(child);
    }
    return this;
  }
}

/**
 * Collapsible section — header is always visible, content toggles.
 */
export class CollapsibleSection extends UIComponent {
  header: HTMLElement;
  content: HTMLElement;
  isExpanded: boolean;
  toggleIcon: HTMLElement;

  constructor(title: string, expanded: boolean = true) {
    super('div');
    this.isExpanded = expanded;

    this.el.style.cssText = `
      border-bottom: 1px solid ${THEME.border};
      background: ${THEME.sidebar};
    `;

    this.header = document.createElement('div');
    this.header.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      cursor: pointer;
      user-select: none;
      background: #fafaf7;
      font-size: 12px;
      font-weight: 600;
      color: ${THEME.text};
      font-family: var(--mono-font);
      transition: background 0.15s;
    `;

    this.toggleIcon = document.createElement('span');
    this.toggleIcon.style.cssText = `
      display: inline-block;
      width: 16px;
      text-align: center;
      transition: transform 0.2s;
      font-size: 10px;
    `;
    this.updateToggleIcon();

    this.header.appendChild(this.toggleIcon);
    const titleEl = document.createElement('span');
    titleEl.textContent = title;
    this.header.appendChild(titleEl);

    this.el.appendChild(this.header);

    this.content = document.createElement('div');
    this.content.style.cssText = `
      padding: 12px 14px;
      max-height: ${expanded ? '1000px' : '0'};
      overflow: hidden;
      transition: max-height 0.25s ease;
    `;
    this.el.appendChild(this.content);

    this.header.addEventListener('click', () => this.toggle());
    this.header.addEventListener('mouseenter', () => {
      this.header.style.background = '#f0f0ed';
    });
    this.header.addEventListener('mouseleave', () => {
      this.header.style.background = '#fafaf7';
    });
  }

  private updateToggleIcon(): void {
    this.toggleIcon.textContent = this.isExpanded ? '▼' : '▶';
  }

  toggle(): void {
    this.isExpanded = !this.isExpanded;
    this.updateToggleIcon();
    this.content.style.maxHeight = this.isExpanded ? '1000px' : '0';
  }

  expand(open: boolean = true): this {
    if (open !== this.isExpanded) {
      this.toggle();
    }
    return this;
  }

  addContent(child: UIComponent | HTMLElement): this {
    if (child instanceof UIComponent) {
      this.content.appendChild(child.el);
    } else {
      this.content.appendChild(child);
    }
    return this;
  }
}

/**
 * Tabbed interface — header with tabs, content swaps on click.
 */
export class TabbedPanel extends UIComponent {
  tabs: Map<string, HTMLElement> = new Map();
  tabButtons: Map<string, HTMLElement> = new Map();
  activeTab: string = '';
  header: HTMLElement;
  content: HTMLElement;

  constructor() {
    super('div');
    this.el.style.cssText = `
      border: 1px solid ${THEME.border};
      border-radius: 6px;
      background: ${THEME.sidebar};
      overflow: hidden;
      display: flex;
      flex-direction: column;
    `;

    this.header = document.createElement('div');
    this.header.style.cssText = `
      display: flex;
      gap: 1px;
      background: #f5f5f2;
      border-bottom: 1px solid ${THEME.border};
      overflow-x: auto;
    `;
    this.el.appendChild(this.header);

    this.content = document.createElement('div');
    this.content.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 0;
      min-height: 0;
    `;
    this.el.appendChild(this.content);
  }

  addTab(id: string, label: string): this {
    // Tab button
    const btn = document.createElement('button');
    btn.style.cssText = `
      flex: 0 0 auto;
      padding: 10px 14px;
      border: none;
      background: transparent;
      color: ${THEME.textMuted};
      font-size: 12px;
      font-weight: 600;
      font-family: var(--mono-font);
      cursor: pointer;
      transition: all 0.15s;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      border-bottom: 2px solid transparent;
    `;

    btn.textContent = label;
    this.tabButtons.set(id, btn);

    btn.addEventListener('click', () => this.switchTab(id));
    btn.addEventListener('mouseenter', () => {
      if (this.activeTab !== id) btn.style.color = THEME.text;
    });
    btn.addEventListener('mouseleave', () => {
      if (this.activeTab !== id) btn.style.color = THEME.textMuted;
    });

    this.header.appendChild(btn);

    // Tab content
    const tabContent = document.createElement('div');
    tabContent.style.cssText = `
      display: none;
      height: 100%;
      overflow-y: auto;
    `;
    this.tabs.set(id, tabContent);
    this.content.appendChild(tabContent);

    // Auto-select first tab
    if (this.activeTab === '') {
      this.switchTab(id);
    }

    return this;
  }

  switchTab(id: string): this {
    // Hide all
    this.tabs.forEach((el) => {
      el.style.display = 'none';
    });
    this.tabButtons.forEach((btn) => {
      btn.style.color = THEME.textMuted;
      btn.style.borderBottomColor = 'transparent';
    });

    // Show active
    const tab = this.tabs.get(id);
    const btn = this.tabButtons.get(id);
    if (tab) {
      tab.style.display = 'flex';
      tab.style.flexDirection = 'column';
    }
    if (btn) {
      btn.style.color = THEME.accent;
      btn.style.borderBottomColor = THEME.accent;
    }

    this.activeTab = id;
    return this;
  }

  getTabContent(id: string): HTMLElement | undefined {
    return this.tabs.get(id);
  }

  addContentToTab(id: string, child: UIComponent | HTMLElement): this {
    const tab = this.tabs.get(id);
    if (!tab) return this;

    if (child instanceof UIComponent) {
      tab.appendChild(child.el);
    } else {
      tab.appendChild(child);
    }
    return this;
  }
}

/**
 * Horizontal flex container for buttons/controls.
 */
export class ButtonGroup extends UIComponent {
  constructor() {
    super('div');
    this.el.style.cssText = `
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      align-items: center;
    `;
  }

  addButton(label: string, onClick?: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = label;
    btn.style.cssText = `
      padding: 8px 12px;
      border: 1px solid ${THEME.border};
      border-radius: 4px;
      background: ${THEME.sidebar};
      color: ${THEME.text};
      font-size: 11px;
      font-family: var(--mono-font);
      cursor: pointer;
      transition: all 0.15s;
    `;

    btn.addEventListener('mouseenter', () => {
      btn.style.background = '#f0f0ed';
      btn.style.borderColor = THEME.text;
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.background = THEME.sidebar;
      btn.style.borderColor = THEME.border;
    });

    if (onClick) btn.addEventListener('click', onClick);
    this.el.appendChild(btn);
    return btn;
  }
}

/**
 * Simple flex row/column container.
 */
export class Flex extends UIComponent {
  constructor(direction: 'row' | 'column' = 'column', gap: number = 8) {
    super('div');
    this.el.style.cssText = `
      display: flex;
      flex-direction: ${direction};
      gap: ${gap}px;
    `;
  }

  setGap(gap: number): this {
    this.el.style.gap = `${gap}px`;
    return this;
  }
}

/**
 * Formatted key-value display.
 */
export class KeyValueDisplay extends UIComponent {
  constructor() {
    super('div');
    this.el.style.cssText = `
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 8px 12px;
      font-size: 11px;
      font-family: var(--mono-font);
      line-height: 1.6;
    `;
  }

  addRow(key: string, value: string | number): this {
    const keyEl = document.createElement('span');
    keyEl.style.cssText = `color: ${THEME.textMuted}; font-weight: 500;`;
    keyEl.textContent = `${key}:`;

    const valEl = document.createElement('span');
    valEl.style.cssText = `color: ${THEME.text}; font-weight: 600;`;
    valEl.textContent = String(value);

    this.el.appendChild(keyEl);
    this.el.appendChild(valEl);

    return this;
  }
}

// ============================================================================
// SPECIALIZED COMPONENTS
// ============================================================================

/**
 * Log viewer — displays messages with timestamp, level icon, and auto-scroll.
 */
export class LogViewer extends UIComponent {
  logs: Array<{ time: Date; level: 'info' | 'success' | 'warning' | 'error'; message: string }> = [];
  maxLogs: number = 100;

  constructor() {
    super('div');
    this.el.style.cssText = `
      height: 200px;
      overflow-y: auto;
      background: #fafaf7;
      border: 1px solid ${THEME.border};
      border-radius: 4px;
      padding: 8px;
      font-size: 11px;
      font-family: var(--mono-font);
      line-height: 1.5;
    `;
  }

  add(message: string, level: 'info' | 'success' | 'warning' | 'error' = 'info'): this {
    this.logs.push({
      time: new Date(),
      level,
      message,
    });

    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
      this.el.removeChild(this.el.firstChild!);
    }

    const line = document.createElement('div');
    line.style.cssText = `
      display: flex;
      gap: 8px;
      align-items: flex-start;
      padding: 4px 0;
      border-bottom: 1px solid #e8e8e0;
    `;

    const time = document.createElement('span');
    time.style.cssText = `color: ${THEME.textDim}; flex-shrink: 0;`;
    const lastLog = this.logs[this.logs.length - 1];
    if (lastLog) {
      time.textContent = lastLog.time.toLocaleTimeString();
    }

    const icon = document.createElement('span');
    icon.style.cssText = `flex-shrink: 0; width: 14px; text-align: center;`;
    const iconMap = {
      info: 'ℹ',
      success: '✓',
      warning: '⚠',
      error: '✗',
    };
    icon.textContent = iconMap[level];
    icon.style.color = {
      info: THEME.textMuted,
      success: '#16a34a',
      warning: '#ff9800',
      error: '#dc2626',
    }[level];

    const msg = document.createElement('span');
    msg.style.cssText = `flex: 1; word-break: break-word; color: ${THEME.text};`;
    msg.textContent = message;

    line.appendChild(time);
    line.appendChild(icon);
    line.appendChild(msg);
    this.el.appendChild(line);

    // Auto-scroll to bottom
    this.el.scrollTop = this.el.scrollHeight;

    return this;
  }

  clear(): this {
    this.logs = [];
    this.el.innerHTML = '';
    return this;
  }

  getLastMessage(): string | null {
    return this.logs.length > 0 ? (this.logs[this.logs.length - 1]?.message ?? null) : null;
  }
}

/**
 * Status indicator — colored dot + text (like LED).
 */
export class StatusIndicator extends UIComponent {
  statusColor: string = THEME.textMuted;

  constructor(label: string) {
    super('div');
    this.el.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 0;
      font-size: 11px;
      font-family: var(--mono-font);
    `;

    const dot = document.createElement('span');
    dot.style.cssText = `
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${this.statusColor};
      flex-shrink: 0;
    `;

    const text = document.createElement('span');
    text.textContent = label;
    text.style.color = THEME.text;

    this.el.appendChild(dot);
    this.el.appendChild(text);
  }

  setStatus(status: 'ok' | 'warning' | 'error'): this {
    const colorMap = {
      ok: '#16a34a',
      warning: '#ff9800',
      error: '#dc2626',
    };
    const dot = this.el.firstChild as HTMLElement;
    if (dot) {
      dot.style.background = colorMap[status];
    }
    return this;
  }
}

// ============================================================================
// BOTTOM PROGRESS BAR (OpenCode / Minimalist style loading bar)
// ============================================================================

export class BottomProgressBar {
  readonly el: HTMLElement;
  private fillEl: HTMLElement;
  private labelEl: HTMLElement;
  private doneLabelEl: HTMLElement;
  private waitingLabelEl: HTMLElement;
  private percentEl: HTMLElement;
  private iconEl: HTMLElement;
  private cancelBtn!: HTMLButtonElement;
  /** TASK 17: invoked when the user clicks Cancel on a running job. */
  private onCancel: (() => void) | null = null;
  private currentProgress = 0;
  private lastStageLabel = '';
  private hideTimeout: number | null = null;

  constructor() {
    this.el = document.createElement('div');
    this.el.id = 'bottom-progress-bar';
    this.el.style.cssText = `
      position: absolute;
      bottom: 24px;
      left: 50%;
      transform: translate(-50%, 8px);
      width: 420px;
      max-width: calc(100% - 280px);
      background: rgba(255, 255, 255, 0.94);
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 10px 16px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      z-index: 25;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      font-family: var(--mono-font), monospace;
      display: none;
    `;

    const doneRow = document.createElement('div');
    doneRow.style.cssText = `
      display: flex;
      align-items: center;
      gap: 7px;
      min-height: 15px;
      margin-bottom: 2px;
      color: #16a34a;
      opacity: 0.55;
      transition: opacity 0.22s ease, transform 0.22s ease;
      font-size: 10px;
    `;
    const doneIcon = document.createElement('span');
    doneIcon.textContent = '✓';
    doneIcon.style.cssText = 'font-weight: 800; font-size: 12px; width: 14px; text-align: center;';
    this.doneLabelEl = document.createElement('span');
    this.doneLabelEl.textContent = 'Ready';
    this.doneLabelEl.style.cssText = 'white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
    doneRow.appendChild(doneIcon);
    doneRow.appendChild(this.doneLabelEl);

    const topRow = document.createElement('div');
    topRow.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 11px;
      color: #1a1a1a;
      margin-bottom: 7px;
    `;

    const leftPart = document.createElement('div');
    // TASK 17 fix: `flex: 1 1 auto; min-width: 0` so this part SHRINKS to make room for the
    // Cancel button. Previously it had no flex sizing, so with `justify-content: space-between`
    // the row stretched Cancel across all remaining space - measured 386px wide for the word
    // "Cancel", with the label stranded at the far left of an empty button.
    leftPart.style.cssText = 'display: flex; align-items: center; gap: 8px; overflow: hidden; flex: 1 1 auto; min-width: 0;';

    this.iconEl = document.createElement('span');
    this.iconEl.style.cssText = `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    `;
    this.iconEl.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style="animation: opencodeSpin 0.75s linear infinite;">
        <circle cx="8" cy="8" r="6" stroke="#e2e8f0" stroke-width="2"/>
        <path d="M14 8a6 6 0 0 0-6-6" stroke="#1a1a1a" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;

    this.labelEl = document.createElement('span');
    this.labelEl.textContent = 'Processing 3D model...';
    this.labelEl.style.cssText = 'font-weight: 500; font-size: 11px; color: #1a1a1a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: -0.01em;';

    leftPart.appendChild(this.iconEl);
    leftPart.appendChild(this.labelEl);

    this.percentEl = document.createElement('span');
    this.percentEl.textContent = '0%';
    this.percentEl.style.cssText = 'font-size: 11px; color: #64748b; font-weight: 600; font-variant-numeric: tabular-nums; flex-shrink: 0; margin-left: 8px;';

    // TASK 17 (2026-09-30): a way OUT of a running preview.
    // Previews take 2-70 s and, until now, the only escape was reloading the page. The
    // Cancel button is hidden unless a job is actually running, so it never appears on a
    // finished or failed bar. Clicking it calls the onCancel handler set via setOnCancel().
    this.cancelBtn = document.createElement('button');
    this.cancelBtn.type = 'button';
    this.cancelBtn.textContent = 'Cancel';
    // TASK 17 fix: index.html has a GLOBAL `button { width: 100% }` rule (written for the
    // home-page module cards), and this button never overrode it - so Cancel stretched to 386px
    // inside the 420px progress bar. Any button added outside that original context must reset
    // the width explicitly. `margin-top: 0` likewise undoes the global `margin-top: 10px`.
    this.cancelBtn.style.cssText = `
      flex: 0 0 auto;
      align-self: center;
      width: auto;
      margin-left: 10px;
      margin-top: 0;
      padding: 3px 10px;
      font-size: 10px;
      font-weight: 600;
      font-family: inherit;
      line-height: 1.4;
      color: #64748b;
      background: transparent;
      border: 1px solid #cbd5e1;
      border-radius: 4px;
      cursor: pointer;
      display: none;
      transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
    `;
    this.cancelBtn.addEventListener('mouseover', () => {
      this.cancelBtn.style.color = '#dc2626';
      this.cancelBtn.style.borderColor = '#fca5a5';
      this.cancelBtn.style.backgroundColor = '#fef2f2';
    });
    this.cancelBtn.addEventListener('mouseout', () => {
      this.cancelBtn.style.color = '#64748b';
      this.cancelBtn.style.borderColor = '#cbd5e1';
      this.cancelBtn.style.backgroundColor = 'transparent';
    });
    this.cancelBtn.addEventListener('click', () => {
      // Hide immediately so a second click cannot fire a second cancel.
      this.cancelBtn.style.display = 'none';
      this.onCancel?.();
    });

    topRow.appendChild(leftPart);
    topRow.appendChild(this.percentEl);
    topRow.appendChild(this.cancelBtn);

    const waitingRow = document.createElement('div');
    waitingRow.style.cssText = `
      display: flex;
      align-items: center;
      gap: 7px;
      min-height: 15px;
      margin-top: 3px;
      color: #94a3b8;
      opacity: 0.48;
      transition: opacity 0.22s ease, transform 0.22s ease;
      font-size: 10px;
    `;
    const waitingIcon = document.createElement('span');
    waitingIcon.textContent = '…';
    waitingIcon.style.cssText = 'font-weight: 800; font-size: 13px; width: 14px; text-align: center;';
    this.waitingLabelEl = document.createElement('span');
    this.waitingLabelEl.textContent = 'Waiting for next step...';
    this.waitingLabelEl.style.cssText = 'white-space: nowrap; overflow: hidden; text-overflow: ellipsis;';
    waitingRow.appendChild(waitingIcon);
    waitingRow.appendChild(this.waitingLabelEl);

    const track = document.createElement('div');
    track.style.cssText = `
      width: 100%;
      margin-top: 9px;
      padding-top: 8px;
      box-sizing: border-box;
    `;

    const trackRail = document.createElement('div');
    trackRail.style.cssText = `
      width: 100%;
      height: 4px;
      background: #f1f5f9;
      border-radius: 999px;
      overflow: hidden;
      position: relative;
    `;

    this.fillEl = document.createElement('div');
    this.fillEl.style.cssText = `
      width: 0%;
      height: 100%;
      background: #1a1a1a;
      border-radius: 999px;
      transition: width 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s ease;
    `;

    trackRail.appendChild(this.fillEl);
    track.appendChild(trackRail);
    this.el.appendChild(doneRow);
    this.el.appendChild(topRow);
    this.el.appendChild(waitingRow);
    this.el.appendChild(track);

    if (!document.getElementById('bottom-progress-style')) {
      const style = document.createElement('style');
      style.id = 'bottom-progress-style';
      style.textContent = `
        @keyframes opencodeSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  /** TASK 17: register what Cancel should do. Called by the app when it wires the bar up. */
  setOnCancel(handler: () => void): this {
    this.onCancel = handler;
    return this;
  }

  start(label = 'Computing 3D geometry (C++ Manifold CSG)...'): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    // TASK 17: only a running job can be cancelled.
    // TASK 17 fix: `block`, not `inline-block`.
    // The button lives inside `topRow`, which is `display:flex` with
    // `justify-content:space-between`. Setting an inline-block display on a flex item makes the
    // browser treat it as a block-level flex item that is free to grow, and space-between then
    // stretched it across the whole remaining row - measured 386px wide for the word "Cancel",
    // with the text stranded in an empty box. `flex:0 0 auto` on the element did not help while
    // this display value was in play.
    this.cancelBtn.style.display = this.onCancel ? 'block' : 'none';
    this.lastStageLabel = '';
    this.doneLabelEl.textContent = 'Started';
    this.waitingLabelEl.textContent = this.nextStageLabel(label);
    this.iconEl.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style="animation: opencodeSpin 0.75s linear infinite;">
        <circle cx="8" cy="8" r="6" stroke="#e2e8f0" stroke-width="2"/>
        <path d="M14 8a6 6 0 0 0-6-6" stroke="#1a1a1a" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
    this.labelEl.textContent = label;
    this.labelEl.style.color = '#1a1a1a';
    this.fillEl.style.background = '#1a1a1a';
    this.currentProgress = 4;
    this.setProgress(this.currentProgress);

    this.el.style.display = 'block';
    // Force layout reflow
    void this.el.offsetWidth;
    this.el.style.opacity = '1';
    this.el.style.transform = 'translate(-50%, 0)';

  }

  /** Update a real preview lifecycle milestone; never fake progress to 85%. */
  stage(progress: number, label: string): void {
    if (this.lastStageLabel && this.lastStageLabel !== label) {
      this.doneLabelEl.textContent = this.lastStageLabel;
    }
    const next = Math.max(this.currentProgress, Math.min(98, progress));
    this.currentProgress = next;
    this.lastStageLabel = label;
    this.labelEl.textContent = label;
    this.waitingLabelEl.textContent = this.nextStageLabel(label);
    this.labelEl.style.color = '#1a1a1a';
    this.setProgress(next);
  }

  finish(successMsg = '✓ 3D model ready'): void {
    // TASK 17: nothing left to cancel once the job is done.
    this.cancelBtn.style.display = 'none';
    this.currentProgress = 100;
    this.doneLabelEl.textContent = successMsg.replace(/^✓\s*/, '');
    this.waitingLabelEl.textContent = 'All steps complete';
    this.setProgress(100);
    this.iconEl.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <path d="M3 8.5L6.5 12L13 4" stroke="#16a34a" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    this.labelEl.textContent = successMsg;
    this.fillEl.style.background = '#16a34a';

    // Hold completed state 400ms so user sees 100% before fade-out
    this.hideTimeout = window.setTimeout(() => {
      this.el.style.opacity = '0';
      this.el.style.transform = 'translate(-50%, 8px)';
      this.hideTimeout = window.setTimeout(() => {
        this.el.style.display = 'none';
        this.setProgress(0);
      }, 250);
    }, 400);
  }

  error(errMsg: string): void {
    // TASK 17: the job already ended (badly) - there is nothing to cancel.
    this.cancelBtn.style.display = 'none';
    this.iconEl.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <path d="M4 4L12 12M12 4L4 12" stroke="#dc2626" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
    this.labelEl.textContent = `Error: ${errMsg}`;
    this.labelEl.style.color = '#dc2626';
    this.fillEl.style.background = '#dc2626';
    this.hideTimeout = window.setTimeout(() => {
      this.el.style.opacity = '0';
      this.el.style.transform = 'translate(-50%, 8px)';
      this.hideTimeout = window.setTimeout(() => {
        this.el.style.display = 'none';
        this.fillEl.style.background = '#1a1a1a';
        this.labelEl.style.color = '#1a1a1a';
        this.setProgress(0);
      }, 250);
    }, 2000);
  }

  /**
   * TASK 17 (2026-09-30): show a short "cancelled" state and hide.
   * Deliberately NOT the same as error(): cancelling is a normal thing the user chose to do,
   * so it gets a neutral message and a brief, non-alarming fade - not a red cross.
   */
  cancel(msg = 'Cancelled'): void {
    this.cancelBtn.style.display = 'none';
    this.iconEl.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6" stroke="#94a3b8" stroke-width="1.6"/>
        <path d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5" stroke="#94a3b8" stroke-width="1.6" stroke-linecap="round"/>
      </svg>
    `;
    this.labelEl.textContent = msg;
    this.labelEl.style.color = '#64748b';
    this.fillEl.style.background = '#94a3b8';

    this.hideTimeout = window.setTimeout(() => {
      this.el.style.opacity = '0';
      this.el.style.transform = 'translate(-50%, 8px)';
      this.hideTimeout = window.setTimeout(() => {
        this.el.style.display = 'none';
        // Restore the neutral palette so the next run starts clean.
        this.fillEl.style.background = '#1a1a1a';
        this.labelEl.style.color = '#1a1a1a';
        this.setProgress(0);
      }, 250);
    }, 700);
  }

  private setProgress(val: number): void {
    const p = Math.round(val);
    this.fillEl.style.width = `${p}%`;
    this.percentEl.textContent = `${p}%`;
  }

  private nextStageLabel(label: string): string {
    if (/Preparing/i.test(label)) return 'Generating geometry...';
    if (/Generating geometry/i.test(label)) return 'Receiving geometry data...';
    if (/Geometry received/i.test(label)) return 'Merging updated model parts...';
    if (/Reusing cached/i.test(label)) return 'Building render meshes...';
    if (/Merging/i.test(label)) return 'Building render meshes...';
    if (/Building render/i.test(label)) return 'Uploading mesh to viewport...';
    if (/Uploading/i.test(label)) return 'Finishing preview...';
    return 'Waiting for next step...';
  }
}
